import { useState, useCallback, useEffect } from "react";
import { cmsApi, type CmsPage, type SeoData } from "@/services/pages";
import UploadInput from "./UploadInput";

interface SeoTabProps {
  pages: CmsPage[];
  siteUrl: string;
  onPagesChange?: (pages: CmsPage[]) => void;
}

interface SeoCheck {
  label: string;
  pass: boolean;
}

function computeScore(seo: Partial<SeoData>, page: CmsPage): { score: number; checks: SeoCheck[] } {
  const title = seo.title || "";
  const desc = seo.description || "";
  const keywords = seo.keywords || "";
  const og = seo.og_image || "";
  const canonical = seo.canonical || "";

  const checks: SeoCheck[] = [
    { label: "Meta title is set", pass: title.length > 0 },
    { label: "Meta title length ≤ 60 chars", pass: title.length > 0 && title.length <= 60 },
    { label: "Meta description is set", pass: desc.length > 0 },
    { label: "Meta description length 120–160 chars", pass: desc.length >= 120 && desc.length <= 160 },
    { label: "Keywords are set", pass: keywords.length > 0 },
    { label: "OG image is set", pass: og.length > 0 },
    { label: "Canonical URL is set", pass: canonical.length > 0 },
    { label: "Page has content sections", pass: page.sections && page.sections.length > 0 },
    { label: "Page has a hero image", pass: !!page.hero_image },
    { label: "Page has a subtitle", pass: !!page.subtitle },
  ];

  const score = Math.round((checks.filter((c) => c.pass).length / checks.length) * 100);
  return { score, checks };
}

function scoreLabel(score: number): { text: string; color: string } {
  if (score >= 80) return { text: "Excellent", color: "text-primary-700" };
  if (score >= 60) return { text: "Needs Work", color: "text-accent-700" };
  if (score >= 40) return { text: "Poor", color: "text-accent-700" };
  return { text: "Critical", color: "text-accent-700" };
}

function scoreRingColor(score: number): string {
  if (score >= 80) return "stroke-primary-500";
  if (score >= 60) return "stroke-accent-500";
  if (score >= 40) return "stroke-accent-500";
  return "stroke-accent-500";
}

export default function SeoTab({ pages, siteUrl, onPagesChange }: SeoTabProps) {
  const [selectedId, setSelectedId] = useState<number | null>(pages[0]?.id ?? null);
  const [draftSeo, setDraftSeo] = useState<Partial<SeoData>>();
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const selectedPage = pages.find((p) => p.id === selectedId) || null;

  useEffect(() => {
    if (selectedPage) {
      setDraftSeo({
        title: selectedPage.seo?.title || "",
        description: selectedPage.seo?.description || "",
        keywords: selectedPage.seo?.keywords || "",
        og_image: selectedPage.seo?.og_image || "",
        canonical: selectedPage.seo?.canonical || "",
      });
    }
  }, [selectedPage?.id]);

  const { score, checks } = selectedPage
    ? computeScore(draftSeo, selectedPage)
    : { score: 0, checks: [] };
  const label = scoreLabel(score);
  const passed = checks.filter((c) => c.pass).length;

  const sitemapUrl = siteUrl
    ? `${siteUrl.replace(/\/$/, "")}/api/seo/sitemap`
    : "/api/seo/sitemap";

  async function autoGenerate() {
    if (!selectedPage) return;
    setGenerating(true);
    setError("");
    try {
      const generated = await cmsApi.generateSeo({
        slug: selectedPage.slug,
        title: selectedPage.title,
        subtitle: selectedPage.subtitle || undefined,
        hero_image: selectedPage.hero_image || undefined,
        sections: selectedPage.sections,
      });
      setDraftSeo((prev) => ({
        ...prev,
        title: generated.title,
        description: generated.description,
        keywords: generated.keywords,
        og_image: prev.og_image || generated.og_image || "",
      }));
    } catch (e: any) {
      setError(e?.message || "Could not generate SEO");
    } finally {
      setGenerating(false);
    }
  }

  async function saveSeo() {
    if (!selectedPage) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await cmsApi.update(selectedPage.id, {
        seo: {
          title: draftSeo.title || "",
          description: draftSeo.description || "",
          keywords: draftSeo.keywords || "",
          og_image: draftSeo.og_image || "",
          canonical: draftSeo.canonical || "",
        },
      });
      setSuccess("SEO saved for this page.");
      // Refresh parent pages list so scores update
      if (onPagesChange) {
        try {
          const refreshed = await cmsApi.list();
          onPagesChange(refreshed);
        } catch {
          // best-effort
        }
      }
    } catch (e: any) {
      setError(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  const base = "w-full rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400";

  return (
    <div className="space-y-5">
      {error && (
        <div className="rounded-2xl bg-accent-100 border border-accent-300 p-4 text-sm text-accent-900">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-2xl bg-primary-50 border border-primary-200 p-4 text-sm text-primary-900">
          {success}
        </div>
      )}

      {/* SEO Manager header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="font-heading text-base text-foreground-950 flex items-center gap-2">
            <i className="ri-seo-line text-accent-600"></i> SEO Manager
          </h3>
          <p className="text-xs text-foreground-600">
            RankMath-style per-page SEO control — title, description, OG, schema, sitemap
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={sitemapUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full border border-background-300 text-foreground-800 px-4 py-2 hover:bg-background-100"
          >
            <i className="ri-eye-line"></i> View Sitemap
          </a>
          <button
            onClick={autoGenerate}
            disabled={generating || !selectedPage}
            className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-accent-100 text-accent-900 border border-accent-200 px-4 py-2 hover:bg-accent-200 disabled:opacity-60"
          >
            <i className={generating ? "ri-loader-4-line animate-spin" : "ri-magic-line"}></i>
            {generating ? "Generating…" : "Auto-generate SEO"}
          </button>
          <button
            onClick={saveSeo}
            disabled={saving || !selectedPage}
            className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-4 py-2 hover:bg-primary-700 disabled:opacity-60"
          >
            <i className={saving ? "ri-loader-4-line animate-spin" : "ri-save-line"}></i>
            {saving ? "Saving…" : "Save SEO"}
          </button>
        </div>
      </div>

      {/* Sitemap card */}
      <div className="rounded-2xl bg-primary-50/60 border border-primary-200/70 p-5">
        <div className="flex items-start gap-4">
          <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary-100 text-primary-700 shrink-0">
            <i className="ri-map-line text-lg"></i>
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="text-sm font-semibold text-foreground-950">
                Dynamic Sitemap (Auto-Updated)
              </h4>
              <span className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-800 text-[11px] font-semibold">
                Live
              </span>
            </div>
            <p className="text-xs text-foreground-600 mb-3">
              Submit this URL to Google Search Console — it auto-updates whenever properties are
              added, edited, or approved.
            </p>
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0 flex items-center gap-2 rounded-lg border border-background-200 bg-background-50 px-3 py-2">
                <i className="ri-link text-foreground-400 text-sm"></i>
                <span className="text-sm text-foreground-700 truncate">{sitemapUrl}</span>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(sitemapUrl);
                  }}
                  className="ml-auto flex items-center justify-center w-7 h-7 rounded-md text-foreground-500 hover:text-primary-700 hover:bg-primary-100"
                  title="Copy URL"
                >
                  <i className="ri-file-copy-line"></i>
                </button>
              </div>
              <a
                href={sitemapUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-4 py-2 hover:bg-primary-700"
              >
                <i className="ri-external-link-line"></i> Open Sitemap
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Pages + SEO Editor */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Pages list */}
        <div className="lg:col-span-1 rounded-2xl bg-background-50 border border-background-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-background-100">
            <h4 className="text-sm font-semibold text-foreground-950">Pages</h4>
          </div>
          <div className="max-h-[600px] overflow-y-auto">
            {pages.length === 0 && (
              <p className="px-4 py-8 text-center text-sm text-foreground-600">No pages yet.</p>
            )}
            {pages.map((p) => {
              const isActive = p.id === selectedId;
              const pScore = computeScore(p.seo || {}, p).score;
              return (
                <button
                  key={p.id}
                  onClick={() => setSelectedId(p.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                    isActive
                      ? "bg-primary-50 border-l-2 border-primary-500"
                      : "hover:bg-background-100 border-l-2 border-transparent"
                  }`}
                >
                  <span
                    className={`flex items-center justify-center w-8 h-8 rounded-lg shrink-0 ${
                      isActive ? "bg-primary-100 text-primary-700" : "bg-background-100 text-foreground-500"
                    }`}
                  >
                    <i className="ri-file-text-line"></i>
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground-950 truncate">{p.title}</p>
                    <p className="text-[11px] text-foreground-600">/{p.slug}</p>
                  </div>
                  <span
                    className={`px-2 py-0.5 rounded-full text-[11px] font-semibold shrink-0 ${
                      pScore >= 80
                        ? "bg-primary-100 text-primary-800"
                        : pScore >= 60
                          ? "bg-accent-100 text-accent-800"
                          : "bg-background-200 text-foreground-700"
                    }`}
                  >
                    {pScore}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* SEO Editor */}
        <div className="lg:col-span-2 rounded-2xl bg-background-50 border border-background-200 overflow-hidden">
          {!selectedPage ? (
            <div className="p-8 text-center text-sm text-foreground-600">
              Select a page from the list to edit its SEO.
            </div>
          ) : (
            <>
              <div className="px-5 py-4 border-b border-background-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-primary-100 text-primary-700">
                      <i className="ri-home-4-line"></i>
                    </span>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-950">
                        {selectedPage.title}
                      </h4>
                      <p className="text-[11px] text-foreground-600">
                        {siteUrl ? `${siteUrl.replace(/\/$/, "")}/${selectedPage.slug}` : `/${selectedPage.slug}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Score ring */}
                    <div className="flex items-center gap-3">
                      <div className="relative w-12 h-12">
                        <svg className="w-12 h-12 -rotate-90" viewBox="0 0 36 36">
                          <path
                            className="stroke-background-200"
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            strokeWidth="3"
                          />
                          <path
                            className={scoreRingColor(score)}
                            strokeDasharray={`${score}, 100`}
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            strokeWidth="3"
                            strokeLinecap="round"
                          />
                        </svg>
                        <span className="absolute inset-0 flex items-center justify-center text-[11px] font-bold text-foreground-800">
                          {score}
                        </span>
                      </div>
                      <div>
                        <p className={`text-sm font-semibold ${label.color}`}>{label.text}</p>
                        <p className="text-[11px] text-foreground-600">SEO Score</p>
                      </div>
                    </div>
                    <span
                      className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${
                        score >= 80
                          ? "bg-primary-100 text-primary-800"
                          : "bg-accent-100 text-accent-800"
                      }`}
                    >
                      {passed}/{checks.length}
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-5 space-y-5">
                {/* SEO Checks */}
                <div>
                  <p className="text-xs font-semibold text-foreground-800 mb-2">
                    SEO Checks ({passed}/{checks.length} passed)
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {checks.map((check, i) => (
                      <div
                        key={i}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${
                          check.pass
                            ? "bg-primary-50 text-primary-800"
                            : "bg-accent-50 text-accent-800"
                        }`}
                      >
                        <i
                          className={
                            check.pass ? "ri-check-line text-primary-600" : "ri-close-line text-accent-600"
                          }
                        ></i>
                        {check.label}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-xs font-semibold text-foreground-800 mb-1.5">
                      Meta title (≤ 60 chars)
                    </label>
                    <input
                      className={base}
                      value={draftSeo.title || ""}
                      onChange={(e) => setDraftSeo((s) => ({ ...s, title: e.target.value }))}
                    />
                    <p className="mt-1 text-[11px] text-foreground-600">
                      {(draftSeo.title || "").length}/60
                    </p>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-semibold text-foreground-800 mb-1.5">
                      Meta description (120–160 chars)
                    </label>
                    <textarea
                      rows={3}
                      className={`${base} resize-none`}
                      value={draftSeo.description || ""}
                      onChange={(e) =>
                        setDraftSeo((s) => ({ ...s, description: e.target.value }))
                      }
                    />
                    <p className="mt-1 text-[11px] text-foreground-600">
                      {(draftSeo.description || "").length}/160
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground-800 mb-1.5">
                      Keywords (comma-separated)
                    </label>
                    <input
                      className={base}
                      value={draftSeo.keywords || ""}
                      onChange={(e) => setDraftSeo((s) => ({ ...s, keywords: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground-800 mb-1.5">
                      OG image URL
                    </label>
                    <input
                      className={base}
                      value={draftSeo.og_image || ""}
                      onChange={(e) => setDraftSeo((s) => ({ ...s, og_image: e.target.value }))}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-semibold text-foreground-800 mb-1.5">
                      Canonical URL
                    </label>
                    <input
                      className={base}
                      value={draftSeo.canonical || ""}
                      onChange={(e) =>
                        setDraftSeo((s) => ({ ...s, canonical: e.target.value }))
                      }
                      placeholder={`${siteUrl || ""}/${selectedPage.slug}`}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-semibold text-foreground-800 mb-1.5">
                      OG Image Upload
                    </label>
                    <UploadInput
                      value={draftSeo.og_image || ""}
                      onChange={(v) => setDraftSeo((s) => ({ ...s, og_image: v }))}
                      type="image"
                      label="Upload OG Image"
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}